import { useRef, useState } from 'react';

import { BrandBar, UnlockList } from '../components/index.js';
import { LegalFooter } from '../components/LegalFooter.js';
import {
  CONSENT,
  EMPLOYEE_BUCKETS,
  FREE_MAIL_ERROR,
  INDUSTRIES,
  OFFERING,
  ROLES,
} from '@shared/config.js';
import { checkBusinessEmail } from '@shared/lead.js';
import type { EmployeeBucketId, RoleId, ScanResult } from '@shared/types.js';

const UNLOCK_ITEMS = [
  'Your full 8-category breakdown, with weights and a prompt for each.',
  `The ${OFFERING.deliverable} as a one-page PDF, yours to keep.`,
  'What a Deep Scan would add — the path from E1 to evidence-based.',
] as const;

export interface ContactFormValues {
  email: string;
  company: string;
  role: RoleId | '';
  industry: string;
  employees: EmployeeBucketId | '';
  marketingConsent: boolean;
}

const EMPTY: ContactFormValues = {
  email: '',
  company: '',
  role: '',
  industry: '',
  employees: '',
  marketingConsent: false,
};

type Errors = Partial<Record<keyof ContactFormValues, string>>;

function validate(values: ContactFormValues): Errors {
  const errors: Errors = {};

  const emailProblem = checkBusinessEmail(values.email);
  if (!values.email.trim()) errors.email = 'Please enter your business email.';
  else if (emailProblem === 'format') errors.email = 'Please enter a valid email address.';
  else if (emailProblem === 'free-mail') errors.email = FREE_MAIL_ERROR;

  if (!values.company.trim()) errors.company = 'Please enter your company.';
  if (!values.role) errors.role = 'Please select your role.';
  if (!values.industry) errors.industry = 'Please select your industry.';
  if (!values.employees) errors.employees = 'Please select your organisation size.';

  // NOTE: marketingConsent is deliberately never validated. It is optional and
  // must never gate submit or the Scorecard (build prompt §8).
  return errors;
}

interface Props {
  result: ScanResult;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (values: ContactFormValues) => void;
}

export default function ContactForm({ result, submitting, submitError, onSubmit }: Props) {
  const [values, setValues] = useState<ContactFormValues>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const formRef = useRef<HTMLFormElement>(null);

  function set<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear a field's error as soon as the visitor edits it, so the red state
    // never lingers on a field they have already corrected.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);

    const firstError = Object.keys(found)[0];
    if (firstError) {
      formRef.current?.querySelector<HTMLElement>(`[name="${firstError}"]`)?.focus();
      return;
    }
    onSubmit(values);
  }

  const fieldProps = (key: keyof Errors) => ({
    className: `field${errors[key] ? ' err' : ''}`,
  });

  return (
    <div className="app">
      <BrandBar tag={`Get your ${OFFERING.deliverable}`} />

      <main className="screen screen--scroll">
        <div className="form-grid">
          <div>
            <span className="kicker">Your result is ready</span>
            <h1
              style={{
                fontSize: 21,
                fontWeight: 600,
                color: 'var(--smart-navy)',
                margin: '8px 0 12px',
                lineHeight: 1.2,
              }}
            >
              Where should we send your Scorecard?
            </h1>
            <p style={{ fontSize: 15, color: 'var(--fg)', lineHeight: 1.6, margin: '0 0 22px' }}>
              You've already got your NDSS of{' '}
              <b>
                {result.overall}, {result.band.label.toLowerCase()}
              </b>
              . Your details unlock the detail and the one-page {OFFERING.deliverable}.
            </p>

            <UnlockList items={UNLOCK_ITEMS} />

            <p
              style={{
                fontSize: 12.5,
                color: 'var(--fg-muted)',
                margin: '18px 0 0',
                lineHeight: 1.5,
              }}
            >
              We block free-mail addresses so the Scorecard reaches your organisation, not a
              personal inbox.
            </p>
          </div>

          <form className="form-card" onSubmit={handleSubmit} ref={formRef} noValidate>
            <div className="form-fields">
              <div {...fieldProps('email')} style={{ gridColumn: '1 / 3' }}>
                <label htmlFor="email">
                  Business email <span className="req">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@yourcompany.com"
                  value={values.email}
                  onChange={(e) => set('email', e.target.value)}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <span className="field-err" id="email-error">
                    {errors.email}
                  </span>
                )}
              </div>

              <div {...fieldProps('company')} style={{ gridColumn: '1 / 3' }}>
                <label htmlFor="company">
                  Company <span className="req">*</span>
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  placeholder="Company name"
                  value={values.company}
                  onChange={(e) => set('company', e.target.value)}
                  aria-invalid={Boolean(errors.company)}
                  aria-describedby={errors.company ? 'company-error' : undefined}
                />
                {errors.company && (
                  <span className="field-err" id="company-error">
                    {errors.company}
                  </span>
                )}
              </div>

              <div {...fieldProps('role')}>
                <label htmlFor="role">
                  Role <span className="req">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={values.role}
                  onChange={(e) => set('role', e.target.value as RoleId)}
                  aria-invalid={Boolean(errors.role)}
                  aria-describedby={errors.role ? 'role-error' : undefined}
                >
                  <option value="">Select…</option>
                  {ROLES.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <span className="field-err" id="role-error">
                    {errors.role}
                  </span>
                )}
              </div>

              <div {...fieldProps('industry')}>
                <label htmlFor="industry">
                  Industry <span className="req">*</span>
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={values.industry}
                  onChange={(e) => set('industry', e.target.value)}
                  aria-invalid={Boolean(errors.industry)}
                  aria-describedby={errors.industry ? 'industry-error' : undefined}
                >
                  <option value="">Select…</option>
                  {INDUSTRIES.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.label}
                    </option>
                  ))}
                </select>
                {errors.industry && (
                  <span className="field-err" id="industry-error">
                    {errors.industry}
                  </span>
                )}
              </div>

              <div {...fieldProps('employees')} style={{ gridColumn: '1 / 3' }}>
                <label htmlFor="employees">
                  Employees <span className="req">*</span>
                </label>
                <select
                  id="employees"
                  name="employees"
                  value={values.employees}
                  onChange={(e) => set('employees', e.target.value as EmployeeBucketId)}
                  aria-invalid={Boolean(errors.employees)}
                  aria-describedby={errors.employees ? 'employees-error' : undefined}
                >
                  <option value="">Select…</option>
                  {EMPLOYEE_BUCKETS.map((bucket) => (
                    <option key={bucket.id} value={bucket.id}>
                      {bucket.label}
                    </option>
                  ))}
                </select>
                {errors.employees && (
                  <span className="field-err" id="employees-error">
                    {errors.employees}
                  </span>
                )}
              </div>
            </div>

            {/* ---------------------------------------------------------------
                CONSENT — two purposes, two separate controls. Never bundled.

                Control 2 (marketing) is an optional, unticked checkbox and is
                rendered FIRST so that Control 1 (the required processing
                statement) sits directly above the submit button, per the
                handoff. Neither control gates the other; submit stays enabled
                while the checkbox is unticked.
                --------------------------------------------------------------- */}
            <div className="consent">
              <label className="check" htmlFor="marketingConsent">
                <input
                  id="marketingConsent"
                  name="marketingConsent"
                  type="checkbox"
                  checked={values.marketingConsent}
                  onChange={(e) => set('marketingConsent', e.target.checked)}
                />
                <span className="box" aria-hidden="true" />
                <span>
                  {CONSENT.marketing.text} <span className="optional-hint">(optional)</span>
                </span>
              </label>

              <p className="consent-req">
                By submitting, you agree that <b>{CONSENT.controller}</b> processes the details
                above to generate and provide the {OFFERING.deliverable}. See our{' '}
                <a href={CONSENT.privacyNoticeUrl} target="_blank" rel="noreferrer">
                  privacy notice
                </a>
                .
              </p>
            </div>

            {submitError && (
              <p className="form-status" role="alert">
                {submitError}
              </p>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  Preparing your Scorecard…
                </>
              ) : (
                <>
                  Get my {OFFERING.deliverable}
                  <span className="btn-arrow" aria-hidden="true" />
                </>
              )}
            </button>

            <p className="consent-micro">
              We keep your details for 12 months unless you ask us to delete them. You can
              withdraw consent at any time at{' '}
              <a href={`mailto:${CONSENT.withdrawalContact}`}>{CONSENT.withdrawalContact}</a>.
            </p>
          </form>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
